<template>
  <div v-loading="loading" class="op-area-hour-panel">
    <p class="mu-legend">
      μ = <b>μ_d</b>(所需车辆 ceil(已订座位/3)) × <b>σ</b>(区域剩车)；σ 为该剩车数下各车边际的均值，按该区域缓冲 K 分箱，上限内步长 2（含上限），超过上限从上限+1 合成一档。本表 S_eff / D / 剩车X 是当前路线口径，X = S_eff − D
    </p>
    <p v-if="missingMuFields" class="mu-stale">
      当前快照没有已订座位 / μ_d / σ。点「立即求解」或重新初始化后会写入这些字段。
    </p>
    <el-table :data="rows" :max-height="tableHeight" class="no-hover" stripe
      empty-text="暂无运营层数据，请先选择路线并完成 EV 推导">
      <el-table-column type="expand">
        <template slot-scope="props">
          <div v-if="!(props.row.bins && props.row.bins.length)" class="bin-empty">
            该时段没有剩车分箱。重启求解器并重新求解后，展开行会列出各档 X 区间、均摊 σ、边际 σ、最终 μ 与 evContrib。
          </div>
          <el-table v-else :data="props.row.bins" size="mini" class="no-hover bin-table" :row-class-name="binRowClass">
            <el-table-column label="档" prop="binIndex" width="50"></el-table-column>
            <el-table-column label="剩车区间 X" width="120">
              <template slot-scope="scope">
                [{{ raw(scope.row.intervalMin) }}, {{ raw(scope.row.intervalMax) }}]
              </template>
            </el-table-column>
            <el-table-column label="均摊σ" width="78">
              <template slot-scope="scope">
                <el-tooltip content="进目标：该档左端各车边际 σ 的均值" placement="top">
                  <span>{{ formatNum(scope.row.sigma) }}</span>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column label="边际σ" width="78">
              <template slot-scope="scope">
                <el-tooltip content="档左端第 X 辆的边际 σ，仅诊断" placement="top">
                  <span>{{ formatNum(scope.row.sigmaMarginal) }}</span>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column label="μ_d×σ̄" width="88">
              <template slot-scope="scope">
                {{ formatNum(scope.row.mu) }}
              </template>
            </el-table-column>
            <el-table-column label="选中" width="60">
              <template slot-scope="scope">
                <span v-if="scope.row.selected" class="selected-tag">是</span>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column label="积分 P" width="80">
              <template slot-scope="scope">
                {{ raw(scope.row.binPoints) }}
              </template>
            </el-table-column>
            <el-table-column label="evCoeff" width="80">
              <template slot-scope="scope">
                {{ formatNum(scope.row.evCoeff) }}
              </template>
            </el-table-column>
            <el-table-column label="收工贡献" width="90">
              <template slot-scope="scope">
                {{ formatNum(scope.row.evContrib) }}
              </template>
            </el-table-column>
          </el-table>
        </template>
      </el-table-column>

      <el-table-column label="时段" prop="slotRange" width="130"></el-table-column>
      <el-table-column label="S_eff" prop="supplyOpEffective" width="70">
        <template slot="header">
          <el-tooltip content="有效供给 = 现存量 S_op + 此前累计收工" placement="top">
            <span>S_eff</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="D" prop="demandCars" width="50">
        <template slot="header">
          <el-tooltip content="本路线该小时窗剩余待覆盖需求车数：座位扣已锁行程后，按每车3座向上取整；只写在窗起点（05:00、06:00），半点行应为 0" placement="top">
            <span>D</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="剩车X" width="70">
        <template slot="header">
          <el-tooltip content="本路线 X = S_eff − D；σ̄ 按该区域 K 分箱，不跟本列重新算" placement="top">
            <span>剩车X</span>
          </el-tooltip>
        </template>
        <template slot-scope="scope">
          <span :class="{ shortage: leftover(scope.row) != null && leftover(scope.row) < 0 }">
            {{ raw(leftover(scope.row)) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="已订座位" width="88">
        <template slot="header">
          <el-tooltip content="起点区域×时钟小时已订座位（含已派）。μ_d 按所需车辆 ceil(座位/3) 计算" placement="top">
            <span>已订座位</span>
          </el-tooltip>
        </template>
        <template slot-scope="scope">
          {{ raw(seats(scope.row)) }}
        </template>
      </el-table-column>
      <el-table-column label="μ_d" width="64" class-name="mu-col">
        <template slot="header">
          <el-tooltip content="已订热度：按所需车辆，1～3 人同为 1 车；2 车=1.0，4 车=1.2，6 车封顶" placement="top">
            <span>μ_d</span>
          </el-tooltip>
        </template>
        <template slot-scope="scope">
          {{ formatNum(muDemand(scope.row)) }}
        </template>
      </el-table-column>
      <el-table-column label="σ" width="64" class-name="mu-col">
        <template slot="header">
          <el-tooltip content="该剩车数下各车边际 σ 的均值，进目标；堆车越多越小，缺车不加溢价" placement="top">
            <span>σ</span>
          </el-tooltip>
        </template>
        <template slot-scope="scope">
          {{ formatNum(sigma(scope.row)) }}
        </template>
      </el-table-column>
      <el-table-column label="μ" width="88" class-name="mu-col">
        <template slot="header">
          <el-tooltip content="最终 μ = μ_d × σ̄" placement="top">
            <span>μ</span>
          </el-tooltip>
        </template>
        <template slot-scope="scope">
          <el-tooltip :content="muTooltip(scope.row)" placement="top">
            <span>{{ formatNum(muFinal(scope.row)) }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="收工EV" width="80">
        <template slot-scope="scope">
          {{ formatNum(scope.row.evObjective) }}
        </template>
      </el-table-column>
      <el-table-column label="Σz" width="70">
        <template slot="header">
          <el-tooltip content="本时段收工车数；若与全局不同会显示全局值" placement="top">
            <span>Σz</span>
          </el-tooltip>
        </template>
        <template slot-scope="scope">
          <span :class="{ 'stop-work-hit': displayStopZ(scope.row) > 0 }">
            {{ formatStopZ(scope.row) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="P" width="70">
        <template slot="header">
          <el-tooltip content="收班有效积分总量" placement="top">
            <span>P</span>
          </el-tooltip>
        </template>
        <template slot-scope="scope">
          {{ raw(scope.row.scoreTotal) }}
        </template>
      </el-table-column>
      <el-table-column label="S_op" prop="supplyOp" width="64">
        <template slot="header">
          <el-tooltip content="按当前路线可服务车辆的现存量" placement="top">
            <span>S_op</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="Σz累计" prop="stopWorkCumPrev" width="76"></el-table-column>
      <el-table-column label="I*" prop="inventoryTarget" width="50">
        <template slot="header">
          <el-tooltip content="前向库存目标，仅用于调位 cap" placement="top">
            <span>I*</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="P_rem" width="80">
        <template slot-scope="scope">
          {{ formatPEffList(scope.row.nodeRemainingPoints) }}
        </template>
      </el-table-column>
      <el-table-column label="P_eff" width="80">
        <template slot-scope="scope">
          {{ formatPEffList(scope.row.precomputedPEff) }}
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
/** 运营层诊断：主表按 μ=μ_d×σ̄ 展示；展开行为剩车分箱（均摊 σ 进目标）。 */
export default {
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    tableHeight: {
      type: Number,
      default: 480
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    missingMuFields() {
      const list = this.rows || [];
      if (!list.length) {
        return false;
      }
      return list.every((row) => row && row.muDemandSeats == null && row.muDemand == null && row.sigma == null);
    }
  },

  methods: {
    raw(val) {
      return val === null || val === undefined ? "-" : val;
    },
    formatNum(val) {
      if (val === null || val === undefined) {
        return "-";
      }
      return this.$options.filters.numFormat(val, 2);
    },
    selectedBin(row) {
      const bins = row && row.bins;
      if (!bins || !bins.length) {
        return null;
      }
      return bins.find((b) => b && b.selected) || null;
    },
    leftover(row) {
      if (row && row.supplyOpEffective != null && row.demandCars != null) {
        return row.supplyOpEffective - row.demandCars;
      }
      return row.surplusOpMu;
    },
    seats(row) {
      return row.muDemandSeats;
    },
    muDemand(row) {
      if (row.muDemand != null) {
        return row.muDemand;
      }
      const bin = this.selectedBin(row);
      if (bin && bin.sigma && bin.mu != null) {
        return bin.mu / bin.sigma;
      }
      return null;
    },
    sigma(row) {
      if (row.sigma != null) {
        return row.sigma;
      }
      const bin = this.selectedBin(row);
      return bin && bin.sigma != null ? bin.sigma : null;
    },
    muFinal(row) {
      if (row.mu != null) {
        return row.mu;
      }
      const md = this.muDemand(row);
      const sg = this.sigma(row);
      if (md != null && sg != null) {
        return md * sg;
      }
      const bin = this.selectedBin(row);
      return bin && bin.sigma != null && bin.mu != null ? bin.mu : null;
    },
    muTooltip(row) {
      return `μ_d × σ̄ = ${this.formatNum(this.muDemand(row))} × ${this.formatNum(this.sigma(row))}`;
    },
    binRowClass({ row }) {
      return row.selected ? "bin-selected-row" : "";
    },
    formatPEffList(list) {
      if (!list || !list.length) {
        return "-";
      }
      const uniq = [...new Set(list)].sort((a, b) => a - b);
      return uniq.join(", ");
    },
    displayStopZ(row) {
      const pathZ = row.stopWorkCount == null ? 0 : row.stopWorkCount;
      const globalZ = row.globalStopWorkCount == null ? pathZ : row.globalStopWorkCount;
      return Math.max(pathZ, globalZ);
    },
    formatStopZ(row) {
      const pathZ = row.stopWorkCount;
      const globalZ = row.globalStopWorkCount;
      if (pathZ == null && globalZ == null) {
        return "-";
      }
      if (globalZ != null && pathZ != null && globalZ !== pathZ) {
        return `${pathZ} (全局${globalZ})`;
      }
      return this.raw(pathZ != null ? pathZ : globalZ);
    }
  }
};
</script>

<style lang="less" scoped>
.mu-legend {
  margin: 0 0 8px;
  color: #c0c4cc;
  font-size: 12px;
  line-height: 1.4;
}

.mu-stale {
  margin: -4px 0 8px;
  color: #e6a23c;
  font-size: 12px;
  line-height: 1.4;
}

.bin-empty {
  padding: 8px 12px;
  color: #909399;
  font-size: 12px;
}

.shortage {
  color: #f56c6c;
  font-weight: 600;
}

.selected-tag {
  color: #409eff;
  font-weight: 600;
}

.stop-work-hit {
  color: #e6a23c;
  font-weight: 600;
}

::v-deep .mu-col {
  background-color: #f4f9ff;
}

::v-deep .bin-selected-row {
  background-color: #ecf5ff !important;
}
</style>
